import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Servicio para manejar el cache busting y evitar problemas con archivos cacheados
 */
@Injectable({
  providedIn: 'root',
})
export class CacheBustingService {
  private cacheVersionSubject = new BehaviorSubject<string>('');
  public cacheVersion$ = this.cacheVersionSubject.asObservable();

  private readonly CACHE_VERSION_KEY = 'app_cache_version';
  private readonly LAST_DEPLOYMENT_KEY = 'last_deployment_check';

  constructor() {
    this.initializeCacheVersion();
    this.setupVersionCheck();
  }

  /**
   * Inicializa la versión del caché
   */
  private initializeCacheVersion(): void {
    const currentVersion = this.getCurrentCacheVersion();
    this.cacheVersionSubject.next(currentVersion);
  }

  /**
   * Configura la verificación periódica de versiones
   */
  private setupVersionCheck(): void {
    // Verificar cada 5 minutos si hay una nueva versión
    setInterval(() => {
      this.checkForNewVersion();
    }, 5 * 60 * 1000);

    // Verificar también cuando la ventana recupera el foco
    window.addEventListener('focus', () => {
      this.checkForNewVersion();
    });

    // Verificar cuando hay cambios en el localStorage de otras pestañas
    window.addEventListener('storage', (event) => {
      if (event.key === this.CACHE_VERSION_KEY) {
        this.handleVersionChange(event.newValue);
      }
    });
  }

  /**
   * Obtiene la versión actual del caché
   * @returns Versión del caché
   */
  private getCurrentCacheVersion(): string {
    return (
      localStorage.getItem(this.CACHE_VERSION_KEY) || this.generateNewVersion()
    );
  }

  /**
   * Genera una nueva versión del caché
   * @returns Nueva versión
   */
  private generateNewVersion(): string {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substr(2, 9);
    const version = `${timestamp}-${random}`;

    localStorage.setItem(this.CACHE_VERSION_KEY, version);
    return version;
  }

  /**
   * Verifica si hay una nueva versión disponible
   */
  private async checkForNewVersion(): Promise<void> {
    try {
      const lastCheck = localStorage.getItem(this.LAST_DEPLOYMENT_KEY);
      const now = Date.now();

      // Solo verificar si han pasado más de 30 minutos desde la última verificación
      if (lastCheck && now - parseInt(lastCheck) < 30 * 60 * 1000) {
        return;
      }

      localStorage.setItem(this.LAST_DEPLOYMENT_KEY, now.toString());

      // Intentar cargar un archivo versionado para verificar si hay cambios
      const response = await fetch(`/main.js?v=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache',
      });

      if (response.ok) {
        const lastModified = response.headers.get('last-modified');
        if (lastModified) {
          const storedLastModified = localStorage.getItem('app_last_modified');

          if (storedLastModified && storedLastModified !== lastModified) {
            // Hay una nueva versión disponible
            this.handleNewVersionDetected();
          }

          localStorage.setItem('app_last_modified', lastModified);
        }
      }
    } catch (error) {
      console.warn('Error verificando nueva versión:', error);
    }
  }

  /**
   * Maneja la detección de una nueva versión
   */
  private handleNewVersionDetected(): void {
    console.log('Nueva versión detectada, limpiando caché...');

    // Generar nueva versión de caché
    const newVersion = this.generateNewVersion();
    this.cacheVersionSubject.next(newVersion);

    // Limpiar caché del navegador
    this.clearBrowserCache();

    // Notificar a otras pestañas
    this.notifyOtherTabs(newVersion);

    // Sugerir recarga después de un pequeño retraso
    setTimeout(() => {
      this.suggestReload();
    }, 2000);
  }

  /**
   * Maneja el cambio de versión desde otra pestaña
   * @param newVersion Nueva versión
   */
  private handleVersionChange(newVersion: string | null): void {
    if (newVersion && newVersion !== this.cacheVersionSubject.value) {
      this.cacheVersionSubject.next(newVersion);
      console.log('Versión actualizada desde otra pestaña:', newVersion);
    }
  }

  /**
   * Notifica a otras pestañas sobre el cambio de versión
   * @param version Nueva versión
   */
  private notifyOtherTabs(version: string): void {
    // Disparar evento de storage para notificar a otras pestañas
    localStorage.setItem(this.CACHE_VERSION_KEY, version);

    // Usar BroadcastChannel si está disponible
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('cache-version');
      channel.postMessage({ type: 'VERSION_UPDATE', version });
      channel.close();
    }
  }

  /**
   * Limpia el caché del navegador
   */
  private clearBrowserCache(): void {
    try {
      // Limpiar caché de Service Worker si está disponible
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.update();
          });
        });
      }

      // Limpiar caché de Cache API
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName);
          });
        });
      }

      // Limpiar datos de webpack/chunks del localStorage
      const keysToRemove = Object.keys(localStorage).filter(
        (key) =>
          key.includes('webpack') ||
          key.includes('chunk') ||
          key.includes('module') ||
          key.startsWith('ng-')
      );

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Error limpiando caché:', error);
    }
  }

  /**
   * Sugiere al usuario recargar la página
   */
  private suggestReload(): void {
    // Solo sugerir recarga si no hay operaciones críticas en curso
    if (this.isSafeToReload()) {
      const shouldReload = confirm(
        'Se ha detectado una nueva versión de la aplicación. ¿Desea recargar la página para aplicar las actualizaciones?'
      );

      if (shouldReload) {
        window.location.reload();
      }
    }
  }

  /**
   * Verifica si es seguro recargar la página
   * @returns true si es seguro recargar
   */
  private isSafeToReload(): boolean {
    // Verificar si hay formularios con datos no guardados
    const forms = document.querySelectorAll('form');
    const hasUnsavedData = Array.from(forms).some((form) => {
      const inputs = form.querySelectorAll('input, textarea, select');
      return Array.from(inputs).some((input) => {
        const element = input as HTMLInputElement;
        return element.value && element.value.trim() !== '';
      });
    });

    return !hasUnsavedData;
  }

  /**
   * Fuerza la limpieza del caché
   */
  forceCacheClear(): void {
    console.log('Forzando limpieza de caché...');

    const newVersion = this.generateNewVersion();
    this.cacheVersionSubject.next(newVersion);

    this.clearBrowserCache();

    // Recargar la página después de limpiar el caché
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  /**
   * Obtiene la versión actual del caché
   * @returns Versión actual
   */
  getCurrentVersion(): string {
    return this.cacheVersionSubject.value;
  }

  /**
   * Obtiene una URL con cache busting
   * @param url URL original
   * @returns URL con parámetro de cache busting
   */
  getCacheBustedUrl(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${this.getCurrentVersion()}`;
  }

  /**
   * Resetea el caché completamente
   */
  resetCache(): void {
    localStorage.removeItem(this.CACHE_VERSION_KEY);
    localStorage.removeItem(this.LAST_DEPLOYMENT_KEY);
    localStorage.removeItem('app_last_modified');

    this.clearBrowserCache();

    const newVersion = this.generateNewVersion();
    this.cacheVersionSubject.next(newVersion);
  }
}
